/**
 * LINE 官方帳號管理（#24-B2 Phase A）。
 * SYSTEM_ADMIN（org 1）：全租戶 view + 可為任一租戶建立/管理；租戶 admin：自 org 單一 OA。
 * 功能：CRUD + 標記 webhook 驗證 + push/broadcast + 訊息日誌 + 月配額用量。
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermission } from '../../../hooks/usePermission';
import { useMessage } from '../../../contexts/MessageContext';
import { useAuth } from '../../../contexts/AuthContext';
import FunctionPageHeader from '../../../components/FunctionPageHeader';
import {
  getOwnOa, listAllOa, createOa, updateOa, deleteOa, verifyWebhook,
  pushMessage, broadcast, getMessages, LineOa, MessageLogResult,
} from './services/lineOaService';

const FUNC = 'ee_line_oa';
const SYSTEM_ADMIN_ORG_ID = 1;

const LineOaPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showApiError } = useMessage();
  const { user } = useAuth();

  const canRead = hasPermission(FUNC, 'read');
  const canCreate = hasPermission(FUNC, 'create');
  const canUpdate = hasPermission(FUNC, 'update');
  const canDelete = hasPermission(FUNC, 'delete');
  const isSystemAdmin = user?.organization_id === SYSTEM_ADMIN_ORG_ID;

  const [all, setAll] = useState<LineOa[]>([]);
  const [current, setCurrent] = useState<LineOa | null>(null);
  const [form, setForm] = useState({ org_id: '', channel_id: '', channel_secret: '', channel_access_token: '', bot_basic_id: '', display_name: '' });
  const [log, setLog] = useState<MessageLogResult | null>(null);
  const [pushTo, setPushTo] = useState(''); const [pushMsg, setPushMsg] = useState('');
  const [bcMsg, setBcMsg] = useState('');

  const loadLog = useCallback(async (id: number) => {
    try { setLog(await getMessages(id)); } catch (err) { showApiError(err); }
  }, [showApiError]);

  const load = useCallback(async () => {
    try {
      if (isSystemAdmin) {
        setAll(await listAllOa());
      } else {
        const oa = await getOwnOa();
        setCurrent(oa);
        if (oa) { fillForm(oa); loadLog(oa.id); }
      }
    } catch (err) { showApiError(err); }
  }, [isSystemAdmin, showApiError, loadLog]);

  useEffect(() => { if (!permLoading && canRead) load(); }, [permLoading, canRead, load]);

  const fillForm = (oa: LineOa) => setForm({
    org_id: String(oa.org_id), channel_id: oa.channel_id, channel_secret: '', channel_access_token: '',
    bot_basic_id: oa.bot_basic_id || '', display_name: oa.display_name || '',
  });

  const manage = (oa: LineOa) => { setCurrent(oa); fillForm(oa); loadLog(oa.id); };

  const save = async () => {
    try {
      if (current) {
        const payload: any = { channel_id: form.channel_id, bot_basic_id: form.bot_basic_id, display_name: form.display_name };
        if (form.channel_secret) payload.channel_secret = form.channel_secret;
        if (form.channel_access_token) payload.channel_access_token = form.channel_access_token;
        const oa = await updateOa(current.id, payload);
        setCurrent(oa);
      } else {
        const payload: any = {
          channel_id: form.channel_id, channel_secret: form.channel_secret,
          channel_access_token: form.channel_access_token, bot_basic_id: form.bot_basic_id, display_name: form.display_name,
        };
        if (isSystemAdmin && form.org_id) payload.org_id = Number(form.org_id);
        const oa = await createOa(payload);
        setCurrent(oa); loadLog(oa.id);
      }
      showSuccess('SYS360001', { name: form.display_name || form.channel_id });
      load();
    } catch (err) { showApiError(err); }
  };

  const doVerify = async () => {
    if (!current) return;
    try { setCurrent(await verifyWebhook(current.id)); showSuccess('SYS360004', {}); } catch (err) { showApiError(err); }
  };
  const doPush = async () => {
    if (!current || !pushTo || !pushMsg) return;
    try { await pushMessage(current.id, pushTo, pushMsg); showSuccess('SYS360002', { to: pushTo }); setPushMsg(''); loadLog(current.id); }
    catch (err) { showApiError(err); }
  };
  const doBroadcast = async () => {
    if (!current || !bcMsg) return;
    try { await broadcast(current.id, bcMsg); showSuccess('SYS360003', {}); setBcMsg(''); loadLog(current.id); }
    catch (err) { showApiError(err); }
  };
  const doDelete = async () => {
    if (!current || !window.confirm(t('common.confirmDelete', { name: current.display_name || current.channel_id }))) return;
    try { await deleteOa(current.id); showSuccess('SYS020003', { name: current.channel_id }); setCurrent(null); load(); }
    catch (err) { showApiError(err); }
  };

  if (permLoading) return null;
  if (!canRead) return (
    <div><FunctionPageHeader funcCode={FUNC} />
      <div style={{ padding: 24, color: '#d32f2f' }}>{t('common.noPermission', '您沒有權限存取此功能')}</div></div>
  );

  const lbl: React.CSSProperties = { fontSize: 13, color: '#555', marginBottom: 2 };
  const inp: React.CSSProperties = { width: '100%', padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4, marginBottom: 10 };
  const btn: React.CSSProperties = { padding: '6px 14px', border: 'none', borderRadius: 4, cursor: 'pointer', color: '#fff', background: '#06c755' };
  const th: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', background: '#f5f5f5', borderBottom: '1px solid #ddd' };
  const td: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid #eee' };

  return (
    <div>
      <FunctionPageHeader funcCode={FUNC} />
      <div style={{ padding: '12px 24px', maxWidth: 900 }}>

        {/* SYSTEM_ADMIN 平台 view：全租戶 OA 列 */}
        {isSystemAdmin && (
          <div style={{ marginBottom: 16 }}>
            <strong>{t('lineOa.allTenants', '全租戶（平台管理）')}</strong>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, marginTop: 8 }}>
              <thead><tr>
                <th style={th}>org</th><th style={th}>{t('lineOa.channelId', 'Channel ID')}</th>
                <th style={th}>{t('lineOa.displayName', '顯示名稱')}</th><th style={th}>{t('lineOa.active', '啟用')}</th>
                <th style={th}>{t('common.actions', '操作')}</th>
              </tr></thead>
              <tbody>
                {all.map((o) => (
                  <tr key={o.id}>
                    <td style={td}>{o.org_id}</td><td style={td}>{o.channel_id}</td>
                    <td style={td}>{o.display_name || '—'}</td><td style={td}>{o.is_active ? '✅' : '—'}</td>
                    <td style={td}><button onClick={() => manage(o)} style={{ ...btn, background: '#1976d2', padding: '2px 10px' }}>{t('common.edit', '管理')}</button></td>
                  </tr>
                ))}
                {all.length === 0 && <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: '#999' }}>{t('lineOa.noOa', '尚未設定 LINE 官方帳號')}</td></tr>}
              </tbody>
            </table>
            <button onClick={() => { setCurrent(null); setForm({ org_id: '', channel_id: '', channel_secret: '', channel_access_token: '', bot_basic_id: '', display_name: '' }); }}
              style={{ ...btn, background: '#455a64', marginTop: 8 }}>+ {t('common.add', '新增')}</button>
          </div>
        )}

        {/* OA 表單（create / edit） */}
        <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <strong>{current ? `${t('common.edit', '管理')} — ${current.channel_id}` : t('common.add', '新增')} {t('lineOa.title', 'LINE 官方帳號')}</strong>
          <div style={{ marginTop: 10 }}>
            {isSystemAdmin && !current && (
              <><div style={lbl}>org_id</div><input style={inp} value={form.org_id} onChange={(e) => setForm({ ...form, org_id: e.target.value })} /></>
            )}
            <div style={lbl}>{t('lineOa.channelId', 'Channel ID')}</div>
            <input style={inp} value={form.channel_id} onChange={(e) => setForm({ ...form, channel_id: e.target.value })} />
            <div style={lbl}>{t('lineOa.channelSecret', 'Channel Secret')} <span style={{ color: '#999' }}>{current ? `（${t('lineOa.secretHint', '留空 = 不更動既有憑證')}）` : ''}</span></div>
            <input style={inp} type="password" value={form.channel_secret} onChange={(e) => setForm({ ...form, channel_secret: e.target.value })} />
            <div style={lbl}>{t('lineOa.accessToken', 'Access Token')} <span style={{ color: '#999' }}>{current ? `（${t('lineOa.secretHint', '留空 = 不更動既有憑證')}）` : ''}</span></div>
            <input style={inp} type="password" value={form.channel_access_token} onChange={(e) => setForm({ ...form, channel_access_token: e.target.value })} />
            <div style={lbl}>{t('lineOa.botBasicId', 'Bot ID（@handle）')}</div>
            <input style={inp} value={form.bot_basic_id} onChange={(e) => setForm({ ...form, bot_basic_id: e.target.value })} />
            <div style={lbl}>{t('lineOa.displayName', '顯示名稱')}</div>
            <input style={inp} value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
            <div style={{ display: 'flex', gap: 8 }}>
              {(current ? canUpdate : canCreate) && <button onClick={save} style={btn}>{t('common.save', '儲存')}</button>}
              {current && canUpdate && <button onClick={doVerify} style={{ ...btn, background: current.webhook_verified_at ? '#2e7d32' : '#ed6c02' }}>
                {current.webhook_verified_at ? t('lineOa.webhookVerified', 'Webhook 已驗證') : t('lineOa.verifyWebhook', '標記已驗證')}</button>}
              {current && canDelete && <button onClick={doDelete} style={{ ...btn, background: '#d32f2f' }}>{t('common.delete', '刪除')}</button>}
            </div>
          </div>
        </div>

        {/* push / broadcast + 訊息日誌（僅選定 OA 後顯示） */}
        {current && canUpdate && (
          <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <strong>{t('lineOa.push', '推播')}</strong>
                <div style={lbl}>{t('lineOa.toUser', '收件 LINE 使用者 ID')}</div>
                <input style={inp} value={pushTo} onChange={(e) => setPushTo(e.target.value)} />
                <div style={lbl}>{t('lineOa.message', '訊息內容')}</div>
                <input style={inp} value={pushMsg} onChange={(e) => setPushMsg(e.target.value)} />
                <button onClick={doPush} style={btn}>{t('lineOa.push', '推播')}</button>
              </div>
              <div style={{ flex: 1, minWidth: 260 }}>
                <strong>{t('lineOa.broadcast', '廣播')}</strong>
                <div style={lbl}>{t('lineOa.message', '訊息內容')}</div>
                <input style={inp} value={bcMsg} onChange={(e) => setBcMsg(e.target.value)} />
                <button onClick={doBroadcast} style={{ ...btn, background: '#ed6c02' }}>{t('lineOa.broadcast', '廣播')}</button>
              </div>
            </div>
          </div>
        )}

        {current && log && (
          <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <strong>{t('lineOa.messageLog', '訊息日誌')}</strong>
              <span style={{ color: log.over_warning ? '#d32f2f' : '#555' }}>
                {t('lineOa.quotaUsage', '本月發送用量')}: {log.quota_used} / {log.quota_limit}
                {log.over_warning ? ` ⚠️ ${t('lineOa.quotaWarning', '接近月配額上限')}` : ''}
              </span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
              <thead><tr>
                <th style={th}>{t('common.time', '時間')}</th><th style={th}>{t('lineOa.directionIn', '接收')}/{t('lineOa.directionOut', '發送')}</th>
                <th style={th}>event</th><th style={th}>status</th>
              </tr></thead>
              <tbody>
                {log.items.map((m) => (
                  <tr key={m.id}>
                    <td style={td}>{m.created_at?.slice(0, 19).replace('T', ' ')}</td>
                    <td style={td}>{m.direction === 'in' ? t('lineOa.directionIn', '接收') : t('lineOa.directionOut', '發送')}</td>
                    <td style={td}>{m.event_type || '—'}</td><td style={td}>{m.reply_status || '—'}</td>
                  </tr>
                ))}
                {log.items.length === 0 && <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#999' }}>—</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LineOaPage;
